import {
  Button,
  Col,
  Flex,
  Input,
  Row,
  Select,
  Table,
  Tag,
  Tooltip,
} from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import useDebounce from "../../../../../../hooks/useDebounce";
import Actions from "../../../../../../redux/actions";
import { ChatsIcon, InfoButtonIcon } from "../../../../../../assets/svg";
import {
  baseRouteMakerForSuperAdmin,
  routes,
} from "../../../../../../constants/routeConsts";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { colors } from "../../../../../../constants/colors";
import Spinner from "../../../../../common/spinner/Spinner";
import { getChatConversationAction } from "../../../../../../redux/services/chat-conversation/chatConversationServices";
import styles from "./ChatConversation.module.scss";
import { userRoles } from "../../../../../../constants";

const ChatConversation = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [currentValue, setCurrentValue] = useState({
    value: userRoles.SERVICE_PROVIDER_ADMIN,
    label: "Service Provider",
  });
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { chatConversations } = useSelector((state) => state.chatConversations);
  const [searchValue, setSearchValue] = useState("");
  const defaultPagination = {
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  }
  const [pagination, setPagination] = useState(defaultPagination);
  const paginationObj = {
    total: chatConversations?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const roleArr = [
    {
      value: userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF,
      label: "Regular",
    },
    {
      value: userRoles.SERVICE_PROVIDER_ADMIN_ROLES.MEDICAL_STAFF,
      label: "Medical",
    },
    {
      value: userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR,
      label: "Doctor",
    },
    {
      value: userRoles.SERVICE_PROVIDER_ADMIN,
      label: "Service Provider",
    },
  ];
  /* filter dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    dispatch(
      Actions.createAction(Actions.GET_CHAT_CONVERSATION_LIST, {
        chatConversations: {},
      })
    );
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      filters: [currentValue?.value],
    };
    dispatch(getChatConversationAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, debouncedSearchValue, currentValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("chats.organization"),
        dataIndex: "name",
        key: "name",
        render: (_, record) =>
          record?.organization ? `${record?.organization?.name}` : "-",
      },
      {
        title: t("chats.fromUser"),
        render: (_, record) =>
          record?.from_user
            ? String(record?.from_user?.first_name).length
              ? `${record?.from_user?.first_name} ${record?.from_user?.last_name}`
              : record?.organization.name + " - Admin"
            : "-",
      },
      {
        title: t("chats.toUser"),
        render: (_, record) =>
          record?.to_user
            ? `${record?.to_user?.first_name} ${record?.to_user?.last_name}`
            : "-",
      },
      {
        title: t("chats.userEmail"),
        dataIndex: "email",
        key: "email",
        render: (_, record) =>
          record?.to_user ? `${record?.to_user?.email}` : "-",
      },
      {
        title: t("chats.userPhone"),
        dataIndex: "phone",
        key: "phone",
        render: (_, record) =>
          record?.to_user?.phone
            ? `+${record?.to_user?.phone?.country_code}-${record?.to_user?.phone?.phone_number}`
            : "-",
      },
      {
        title: t("dashboard.status"),
        key: "action",
        align: "center",
        render: (_, record) => {
          const getColorBasedOnStatus = (status) => {
            switch (status.toLowerCase()) {
              case "pending":
                return { background: colors.lightPeach, color: colors.salmon };

              case "open":
                return {
                  background: colors.powderBlue,
                  color: colors.oceanBlue,
                };

              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={getColorBasedOnStatus(record?.status)}
              >
                {String(record?.status).toUpperCase()}
              </Tag>
            </>
          );
        },
      },
      {
        title: (
          <>
            <Flex gap="1rem" align="center" justify="center">
              <>{t("chats.chats")}</>
              <Tooltip
                placement="topLeft"
                title={t("Modules.TicketManagement.chatsTooltipInfo")}
                color={colors.DarkMint}
              >
                <Button type="text" shape="circle" size="small">
                  <Flex align="center" justify="center">
                    <InfoButtonIcon />
                  </Flex>
                </Button>
              </Tooltip>
            </Flex>
          </>
        ),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Button
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "auto",
                }}
                type="dashed"
                size="medium"
                icon={<ChatsIcon fillColor={colors.primaryColor} />}
                onClick={(e) => handleChatDetails(e, record)}
              />
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* initial dependencies rendering */

  const handleChatDetails = (e, row) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_CHAT_CONVERSATION_ID, {
        selectedChatConversationId: "",
      })
    );
    navigate(
      baseRouteMakerForSuperAdmin(routes.CHAT_ACCESS_CHAT_CONVERSATION_CHAT)
    );
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedChatConversationId = row?._id;
      dispatch(
        Actions.createAction(Actions.SELECTED_CHAT_CONVERSATION_ID, {
          selectedChatConversationId,
        })
      );
    }
  };

  return (
    <div className={styles.ticketManagementContainer}>
      <Flex vertical gap="2rem">
        <Row gutter={[24]}>
          <Col span={7} style={{ display: "flex", alignItems: "end" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("CommonUtils.search")}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
          <Col
            span={7}
            style={{ display: "flex", alignItems: "end", marginBottom: "0px" }}
          >
            <Select
              style={{ width: "100%" }}
              showSearch
              allowClear={false}
              options={[
                {
                  value: userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF,
                  label: "Regular",
                },
                {
                  value: userRoles.SERVICE_PROVIDER_ADMIN_ROLES.MEDICAL_STAFF,
                  label: "Medical",
                },
                {
                  value: userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR,
                  label: "Doctor",
                },
                {
                  value: userRoles.SERVICE_PROVIDER_ADMIN,
                  label: "Service Provider",
                },
              ]}
              placeholder="Select Role"
              onChange={(value) => {
                const selectedRoleObj = roleArr?.find(
                  (element) => element.value === value
                );
                setCurrentValue(selectedRoleObj);
                setPagination(defaultPagination)
              }}
              value={currentValue}
            />
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table
            dataSource={chatConversations?.data}
            columns={columns}
            pagination={paginationObj}
          />
        )}
      </Flex>
    </div>
  );
};

export default ChatConversation;
